/* global GOOGLE_ANALYTICS_ID */
/**
 * GA4 identifier capture, storage, fallback and formatting (SPEC-027).
 *
 * Owns the browser-side `client_id` / `session_id` capture via gtag, applies
 * fallbacks when blockers prevent capture, and enforces Alex's formatting
 * contract (client_id String, session_id Number, user_id String CRM id).
 */

// Webpack global, injected at build time. Guarded so importing this module
// never throws in environments where it isn't defined (e.g. jest).
const MEASUREMENT_ID =
  typeof GOOGLE_ANALYTICS_ID !== 'undefined' ? GOOGLE_ANALYTICS_ID : undefined // 'G-ZB06X08W1Y' in prod
const CAPTURE_TIMEOUT_MS = 800

let captured = { client_id: null, session_id: null }
// Holds the in-flight (or resolved) capture so concurrent callers share one
// run instead of racing the module-level cache. Null means "not yet captured".
let capturePromise = null

const getViaGtag = (key) =>
  new Promise((resolve) => {
    let settled = false
    let timer
    const finish = (value) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve(value)
    }
    window.gtag('get', MEASUREMENT_ID, key, (value) => finish(value ?? null))
    timer = setTimeout(() => finish(null), CAPTURE_TIMEOUT_MS)
  })

// Capture both identifiers once gtag is available, caching the result for the
// rest of the app load (use resetGa4Capture to force a refresh on login/logout).
// If gtag has not been injected yet (GTM still loading), this does NOT latch —
// it returns the current values and a later call retries once gtag is ready, so
// a real client_id is captured rather than permanently falling back.
export const captureGa4Identifiers = async () => {
  if (typeof window.gtag !== 'function') return captured
  if (capturePromise) return capturePromise
  capturePromise = (async () => {
    try {
      const [clientId, sessionId] = await Promise.all([
        getViaGtag('client_id'),
        getViaGtag('session_id')
      ])
      captured = {
        client_id: clientId, // string or null
        session_id: sessionId != null ? Number(sessionId) : null
      }
      // gtag was present but answered with nothing (callback never fired
      // within CAPTURE_TIMEOUT_MS — e.g. a blocker stubbed gtag with a no-op).
      // Don't latch the empty result: free the in-flight token so a later call
      // retries and a real client_id can still be captured. Mirrors the
      // no-gtag guard above; without this the session is stuck on fallbacks.
      if (clientId == null) capturePromise = null
    } catch (_) {
      // gtag threw — keep fallbacks and allow a later attempt to retry.
      capturePromise = null
    }
    return captured
  })()
  return capturePromise
}

// Returns the identifier triple with FR-2 fallbacks and FR-4 formatting.
// client_id / user_id are null for anonymous users with capture blocked;
// callers (pushGa4Event) omit null fields per decision D1.
export const getGa4Identifiers = (userId) => {
  const uid = userId != null ? String(userId) : null
  return {
    client_id: captured.client_id ?? uid, // FR-2 fallback
    session_id: captured.session_id ?? Math.floor(Date.now() / 1000), // FR-2 fallback (Number)
    user_id: uid // omitted by caller if null (anon)
  }
}

// Force-refresh after login/logout so a fresh capture re-runs.
export const resetGa4Capture = () => {
  capturePromise = null
}
